import React, { useState, useCallback, useMemo } from "react";
import {
  Download,
  QrCode as QrCodeIcon,
  X,
  Smartphone,
  CheckCircle,
  Apple,
} from "lucide-react";
import QRCode from "react-qr-code";
import AppleLogo from "src/img/Apple_logo_white.png";
import { useTranslation } from "react-i18next";
import "./Downloads.css";
import i18next from "i18next";

interface Version {
  version: string;
  androidUrl: string;
  iosUrl: string;
  releaseDate: string;
  size: {
    android: string;
    ios: string;
  };
  features: string[];
}

const Downloads = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeQRUrl, setActiveQRUrl] = useState("");
  const [activeVersion, setActiveVersion] = useState("");
  const [modalPlatform, setModalPlatform] = useState<"ios" | "android">("android");
  const { t } = useTranslation();

  const versions: Version[] = useMemo(() => [{
    version: `1.0.0 ${t("download.stable_version")}`,
    androidUrl: "https://play.google.com/store/apps/details?id=com.idiaz18.wizard&pcampaignid=web_share",
    iosUrl: "https://apps.apple.com/es/app/wizard-football/id6740484602",
    releaseDate: i18next.language === "es" ? "Abril 2026" : "April 2026",
    size: { android: "83.7 MB", ios: "37,8 MB" },
    features: [
      t("download.features_versions.4k_recording"),
      t("download.features_versions.live_streaming"),
      t("download.features_versions.ai_analysis"),
      t("download.features_versions.match_statistics"),
    ],
  }], [t]);

  // Intersection Observer setup
  // useEffect(() => {
  //   const elementsToObserve = [headerRef, featuresRef, versionCardRef];
  //   const observer = new IntersectionObserver(
  //     (entries) => {
  //       entries.forEach((entry) => {
  //         if (entry.isIntersecting) {
  //           const elementId = entry.target.getAttribute("data-animate");
  //           if (elementId) {
  //             setAnimatedElements(prev => new Set([...prev, elementId]));
  //           }
  //         }
  //       });
  //     },
  //     {
  //       threshold: 0.1,
  //       rootMargin: "50px",
  //     }
  //   );

  //   elementsToObserve.forEach((ref) => {
  //     if (ref.current) observer.observe(ref.current);
  //   });

  //   return () => {
  //     elementsToObserve.forEach((ref) => {
  //       if (ref.current) observer.unobserve(ref.current);
  //     });
  //   };
  // }, []);

  const handleShowQRCode = useCallback((url: string, version: string, platform: "ios" | "android") => {
    setActiveQRUrl(url);
    setActiveVersion(version);
    setModalPlatform(platform);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleBackdropClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  }, [closeModal]);

  // Modal content helpers
  const modalIcon = useMemo(() => (
    modalPlatform === "android" ? (
      <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
        <Smartphone className="w-8 h-8 text-white" />
      </div>
    ) : (
      <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
        <QrCodeIcon className="w-8 h-8 text-white" />
      </div>
    )
  ), [modalPlatform]);

  const modalTitle = useMemo(() => (
    modalPlatform === "android"
      ? t("download.android_download")
      : t("download.donwload_for_ios")
  ), [modalPlatform, t]);

  const modalDescription = useMemo(() => (
    modalPlatform === "android"
      ? t("download.scan_code_android") || "Scan this QR code with your Android device to download the app"
      : t("download.scan_code_ios")
  ), [modalPlatform, t]);

  const modalLink = useMemo(() => (
    modalPlatform === "android" ? (
      <a
        href={activeQRUrl}
        className="inline-flex items-center gap-2 text-green-600 hover:text-green-800 font-medium transition-colors"
        target="_blank"
        rel="noopener noreferrer"
      >
        <Smartphone className="w-4 h-4" />
        {t("download.open_in_play_store") || "Open in Play Store"}
      </a>
    ) : (
      <a
        href={activeQRUrl}
        className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium transition-colors"
        target="_blank"
        rel="noopener noreferrer"
      >
        <Apple className="w-4 h-4" />
        {t("download.open_in_app_store")}
      </a>
    )
  ), [modalPlatform, activeQRUrl, t]);

  // Features list for reuse
  const featureItems = useMemo(() => [
    t("download.features_versions.4k_recording"),
    t("download.features_versions.live_streaming"),
    t("download.features_versions.ai_analysis"),
    t("download.features_versions.match_statistics"),
  ], [t]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 py-20 px-4 overflow-hidden">
      <div className="max-w-6xl mx-auto">

        {/* Header Section */}
        <HeaderSection />

        {/* Features Grid */}
        <FeaturesGrid featureItems={featureItems} />

        {/* Version Cards */}
        <VersionCards versions={versions} handleShowQRCode={handleShowQRCode} />
      </div>

      {/* QR Code Modal */}
      <QRModal
        isModalOpen={isModalOpen}
        modalIcon={modalIcon}
        modalTitle={modalTitle}
        modalDescription={modalDescription}
        modalLink={modalLink}
        activeQRUrl={activeQRUrl}
        activeVersion={activeVersion}
        closeModal={closeModal}
        handleBackdropClick={handleBackdropClick}
      />
    </div>
  );
};

// Extracted Header Component
const HeaderSection: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="text-center mb-16">
      <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl mb-6 shadow-xl">
        <Download className="w-10 h-10 text-white" />
      </div>
      <h1 className="text-5xl md:text-6xl font-bold text-slate-800 mb-4">
        {t("download.title")}
      </h1>
      <p className="text-xl text-slate-600 max-w-2xl mx-auto">
        {t("download.description")}
      </p>
      <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 mx-auto rounded-full mt-6" />
    </div>
  );
};

// Extracted Features Grid Component
const FeaturesGrid: React.FC<{
  featureItems: string[];
}> = ({ featureItems }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
    {featureItems.map((feature) => (
      <div
        key={feature}
        className="text-center p-4 bg-white/60 backdrop-blur-sm rounded-xl border border-white/20"
      >
        <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
        <p className="text-sm font-medium text-slate-700">{feature}</p>
      </div>
    ))}
  </div>
);

// Extracted Version Cards Component
const VersionCards: React.FC<{
  versions: Version[];
  handleShowQRCode: (url: string, version: string, platform: "ios" | "android") => void;
}> = ({ versions, handleShowQRCode }) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-8">
      {versions.map((version) => (
        <div key={version.version} className="relative">
          <div className="bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 md:p-12 relative overflow-hidden">
            {/* Background decorations */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full opacity-30 transform translate-x-32 -translate-y-32" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-green-100 to-emerald-100 rounded-full opacity-20 transform -translate-x-24 translate-y-24" />

            <div className="relative z-10">
              <VersionHeader version={version} />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <DownloadCard
                  platform="android"
                  version={version}
                  handleShowQRCode={handleShowQRCode}
                />
                <DownloadCard
                  platform="ios"
                  version={version}
                  handleShowQRCode={handleShowQRCode}
                />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Extracted Version Header Component
const VersionHeader: React.FC<{
  version: Version;
}> = ({ version }) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8">
      <div className="flex items-center gap-4 mb-4 sm:mb-0">
        <div className="w-4 h-4 bg-green-500 rounded-full animate-pulse" />
        <h2 className="text-2xl md:text-3xl font-bold text-slate-800">
          {t("download.version.title")} {version.version}
        </h2>
      </div>
      <div className="text-right text-sm text-slate-600">
        <div>{version.releaseDate}</div>
        <div className="font-medium">
          {t("download.version.latest_release")}
        </div>
      </div>
    </div>
  );
};

// Extracted Download Card Component
const DownloadCard: React.FC<{
  platform: "android" | "ios";
  version: Version;
  handleShowQRCode: (url: string, version: string, platform: "ios" | "android") => void;
}> = ({ platform, version, handleShowQRCode }) => {
  const { t } = useTranslation();

  const isAndroid = platform === "android";
  const colors = isAndroid
    ? {
      gradient: "from-green-500 to-emerald-600",
      hoverGradient: "from-green-600 to-emerald-700",
      border: "border-green-200 hover:border-green-400",
      text: "text-green-600 hover:text-green-800",
      bg: "bg-gradient-to-r from-green-400 to-emerald-500",
      size: version.size.android
    }
    : {
      gradient: "from-blue-500 to-indigo-600",
      hoverGradient: "from-blue-600 to-indigo-700",
      border: "border-blue-200 hover:border-blue-400",
      text: "text-blue-600 hover:text-blue-800",
      bg: "bg-gradient-to-r from-blue-400 to-indigo-500",
      size: version.size.ios
    };

  const title = isAndroid ? t("download.android_download") : t("download.donwload_for_ios");
  const description = isAndroid ? t("download.android_compatible") : t("download.compatible_with_ios");
  const url = isAndroid ? version.androidUrl : version.iosUrl;

  return (
    <div className="group relative">
      <div className={`absolute inset-0 ${colors.bg} rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />
      <div className={`relative bg-white/80 backdrop-blur-sm border-2 ${colors.border} rounded-2xl p-8 text-center hover:shadow-xl transition-all duration-300`}>
        <div className={`w-16 h-16 ${colors.gradient} rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg`}>
          <Smartphone className="w-8 h-8 text-black" />
        </div>
        <h3 className="text-xl font-bold text-slate-800 mb-2">{title}</h3>
        <p className="text-slate-600 mb-6">{description}</p>
        <div className="text-sm text-slate-500 mb-6">
          {t("download.ios_size")}: {colors.size}
        </div>
        <div className="space-y-3">
          {/* <a
            href={url}
            className={`inline-flex items-center justify-center gap-3 w-full ${colors.gradient} text-white py-4 px-6 rounded-xl font-semibold hover:${colors.hoverGradient} transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 group`}
          >
            {!isAndroid && (
              <img
                src={AppleLogo}
                className="w-5 h-5 object-contain"
                alt="apple logo"
                loading="lazy"
              />
            )}
            {isAndroid ? "Play Store" : t("home.app_store")}
          </a> */}
          <button
            onClick={() => handleShowQRCode(url, version.version, platform)}
            className={`flex items-center justify-center gap-3 w-full ${colors.text} transition-colors border-2 ${colors.border} hover:${colors.border.replace('200', '400')} py-3 px-6 rounded-xl hover:bg-${isAndroid ? 'green' : 'blue'}-50 font-medium`}
          >
            <QrCodeIcon className="w-5 h-5" />
            {t("home.qr_code")}
          </button>
        </div>
      </div>
    </div>
  );
};

// Extracted QR Modal Component
const QRModal: React.FC<{
  isModalOpen: boolean;
  modalIcon: React.ReactNode;
  modalTitle: string;
  modalDescription: string;
  modalLink: React.ReactNode;
  activeQRUrl: string;
  activeVersion: string;
  closeModal: () => void;
  handleBackdropClick: (e: React.MouseEvent<HTMLDivElement>) => void;
}> = ({
  isModalOpen,
  modalIcon,
  modalTitle,
  modalDescription,
  modalLink,
  activeQRUrl,
  activeVersion,
  closeModal,
  handleBackdropClick
}) => {
    const { t } = useTranslation();

    if (!isModalOpen) return null;

    return (
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={handleBackdropClick}
      >
        <div className="bg-white/95 backdrop-blur-lg rounded-3xl p-8 max-w-md w-full shadow-2xl border border-white/20 relative">
          <button
            onClick={closeModal}
            className="absolute top-4 right-4 p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-all"
            aria-label="Close modal"
          >
            <X className="w-6 h-6" />
          </button>

          <div className="text-center">
            {modalIcon}

            <h3 className="text-2xl font-bold text-slate-800 mb-2">
              {modalTitle}
            </h3>
            <p className="text-slate-600 mb-8">{modalDescription}</p>

            <div className="flex justify-center mb-6">
              <div className="p-4 bg-white rounded-2xl shadow-lg border border-slate-200">
                <QRCode value={activeQRUrl} size={200} />
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                {t("download.version.title")} {activeVersion}
              </p>
              {modalLink}
            </div>
          </div>
        </div>
      </div>
    );
  };

export default Downloads;